import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import {
    X, ArrowLeft, ArrowRight, Check, Copy,
    QrCode, Link2, Users, Radar, Sparkles, AlertCircle
} from 'lucide-react';
import { contactService } from '../services/contactService';
import { Contact } from '../types';


interface AddContactFlowProps {
    isOpen: boolean;
    onClose: () => void;
    onAddContact: (id: string) => void;
    myId: string;
    myName: string;
}

type AddMethod = 'choose' | 'qr' | 'link' | 'id' | 'nearby';
type Step = 1 | 2 | 3 | 4;

const AddContactFlow: React.FC<AddContactFlowProps> = ({
    isOpen,
    onClose,
    onAddContact,
    myId,
    myName
}) => {
    const [method, setMethod] = useState<AddMethod>('choose');
    const [step, setStep] = useState<Step>(1);
    const [contactId, setContactId] = useState('');
    const [linkCopied, setLinkCopied] = useState(false);
    const [relationship, setRelationship] = useState<string>('friend');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [foundName, setFoundName] = useState<string | null>(null);

    // NEW STATE
    const [nearbyUsers, setNearbyUsers] = useState<Contact[]>([]);
    const [isScanningNearby, setIsScanningNearby] = useState(false);

    const shareLink = `${window.location.origin}?add=${myId}`;

    // QR SCANNER LOGIC
    useEffect(() => {
        if (method === 'qr') {
            const scanner = new Html5QrcodeScanner(
                "reader",
                { fps: 10, qrbox: { width: 250, height: 250 } },
                /* verbose= */ false
            );

            scanner.render(async (decodedText) => {
                console.log("Scanned:", decodedText);
                let scannedId = decodedText;
                if (decodedText.includes('?add=')) {
                    scannedId = decodedText.split('?add=')[1];
                }
                scannedId = scannedId.trim();

                try {
                    await scanner.clear();
                } catch (e) {
                    console.warn("Failed to clear scanner", e);
                }

                setContactId(scannedId);
                verifyAndAdd(scannedId);

            }, (error) => {
                // console.warn(error);
            });

            return () => {
                scanner.clear().catch(console.error);
            };
        }
    }, [method]);

    // NEARBY LOGIC
    useEffect(() => {
        if (method === 'nearby') {
            setIsScanningNearby(true);
            const scan = async () => {
                try {
                    await new Promise(r => setTimeout(r, 2000));
                    const users = await contactService.getAllNearbyUsers(myId);
                    setNearbyUsers(users);
                } catch (e) {
                    console.error("Nearby scan failed", e);
                } finally {
                    setIsScanningNearby(false);
                }
            };
            scan();
        }
    }, [method, myId]);

    const verifyAndAdd = async (idOfUser: string) => {
        setIsSearching(true);
        let finalId = idOfUser;
        if (idOfUser.length === 6) {
            const user = await contactService.findUserByNid(idOfUser);
            if (user) finalId = user.id;
        }

        onAddContact(finalId);
        setStep(4);
        setIsSearching(false);
    };

    const copyLink = async () => {
        await navigator.clipboard.writeText(shareLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    const shareNative = async () => {
        if (navigator.share) {
            await navigator.share({
                title: 'Connect on Protectly',
                text: `${myName} wants to add you as a contact on Protectly - Digital Guardian`,
                url: shareLink,
            });
        } else {
            copyLink();
        }
    };

    const handleSubmitId = async () => {
        if (!contactId.trim()) return;

        setIsSearching(true);
        setSearchError(null);

        // Check if Short NID (6 chars) or Long UUID
        const input = contactId.trim();
        let targetId = input;

        if (input.length === 6) {
            const user = await contactService.findUserByNid(input);
            if (user) {
                targetId = user.id;
                setFoundName(user.name);
            } else {
                setSearchError("User ID not found");
                setIsSearching(false);
                return;
            }
        } else if (input.length < 30) {
            // Likely invalid
            setSearchError("Invalid ID format (Use 6-char NID)");
            setIsSearching(false);
            return;
        }

        onAddContact(targetId);
        setStep(4);
        setIsSearching(false);
    };

    const resetAndClose = () => {
        setMethod('choose');
        setStep(1);
        setContactId('');
        setRelationship('friend');
        onClose();
    };

    const methods = [
        { id: 'qr', icon: <QrCode className="w-8 h-8" />, label: 'Scan QR', desc: 'Scan their QR code', color: 'cyan' },
        { id: 'link', icon: <Link2 className="w-8 h-8" />, label: 'Share Link', desc: 'Send invite link', color: 'green' },
        { id: 'id', icon: <Users className="w-8 h-8" />, label: 'Enter NID', desc: 'Type 6-char Short ID', color: 'purple' },
        { id: 'nearby', icon: <Radar className="w-8 h-8" />, label: 'Nearby', desc: 'Find nearby users', color: 'yellow' },
    ];

    const relationships = [
        { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦', desc: 'Parents, siblings, children' },
        { id: 'friend', label: 'Friend', icon: '🤝', desc: 'Close friends, roommates' },
        { id: 'colleague', label: 'Colleague', icon: '💼', desc: 'Work, school, team' },
        { id: 'emergency', label: 'Emergency', icon: '🆘', desc: 'Emergency contact' },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-xl"
                >
                    {/* Header */}
                    <header className="flex items-center justify-between p-4 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            {method !== 'choose' && step < 4 && (
                                <button
                                    onClick={() => { setMethod('choose'); setStep(1); }}
                                    className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                                >
                                    <ArrowLeft className="w-5 h-5 text-gray-400" />
                                </button>
                            )}
                            <div>
                                <h1 className="text-lg font-bold text-white">Add Contact</h1>
                                <p className="text-[10px] text-gray-500 tracking-widest">
                                    {method === 'choose' ? 'CHOOSE METHOD' :
                                        method === 'qr' ? 'SCAN QR CODE' :
                                            method === 'link' ? 'SHARE INVITE' :
                                                method === 'id' ? 'ENTER CONTACT ID' : 'NEARBY DISCOVERY'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={resetAndClose}
                            className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/50 transition-all"
                        >
                            <X className="w-5 h-5 text-gray-400" />
                        </button>
                    </header>

                    {/* Progress Indicator */}
                    {method !== 'choose' && (
                        <div className="flex items-center justify-center gap-2 py-4">
                            {[1, 2, 3, 4].map((s) => (
                                <div
                                    key={s}
                                    className={`h-1 w-12 rounded-full transition-all ${s <= step
                                        ? 'bg-[var(--neon-cyan)] shadow-[0_0_10px_rgba(0,240,255,0.5)]'
                                        : 'bg-white/10'
                                        }`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <AnimatePresence mode="wait">
                            {/* Step 1: Choose Method */}
                            {method === 'choose' && (
                                <motion.div
                                    key="choose"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="grid grid-cols-2 gap-4"
                                >
                                    {methods.map((m) => (
                                        <motion.button
                                            key={m.id}
                                            onClick={() => { setMethod(m.id as AddMethod); setStep(1); }}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className={`p-6 rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-${m.color === 'cyan' ? '[var(--neon-cyan)]' : m.color}-500/50 transition-all text-center group`}
                                        >
                                            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${m.color === 'cyan' ? '[var(--neon-cyan)]' : m.color}-500/10 flex items-center justify-center text-${m.color === 'cyan' ? '[var(--neon-cyan)]' : m.color}-400 group-hover:scale-110 transition-transform`}>
                                                {m.icon}
                                            </div>
                                            <h3 className="text-white font-bold mb-1">{m.label}</h3>
                                            <p className="text-gray-500 text-xs">{m.desc}</p>
                                        </motion.button>
                                    ))}
                                </motion.div>
                            )}

                            {/* Share Link Method */}
                            {method === 'link' && (
                                <motion.div
                                    key="link"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="flex flex-col items-center"
                                >
                                    {/* My QR Code */}
                                    <div className="bg-white p-5 rounded-3xl shadow-[0_0_40px_rgba(0,240,255,0.2)] mb-6">
                                        <QRCodeSVG value={shareLink} size={200} level="M" />
                                    </div>
                                    <p className="text-center text-gray-400 text-sm mb-6">
                                        Share this link or let them scan your QR
                                    </p>

                                    {/* Link Display */}
                                    <div className="w-full bg-black/50 border border-white/10 rounded-2xl p-4 mb-4">
                                        <p className="text-[10px] text-gray-500 mb-2 tracking-widest">YOUR INVITE LINK</p>
                                        <p className="text-gray-300 font-mono text-sm truncate">{shareLink}</p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="w-full grid grid-cols-2 gap-3">
                                        <button
                                            onClick={copyLink}
                                            className={`flex items-center justify-center gap-2 py-4 rounded-2xl font-bold transition-all ${linkCopied
                                                ? 'bg-green-500 text-black'
                                                : 'bg-white/5 border border-white/10 text-white hover:border-[var(--neon-cyan)]/50'
                                                }`}
                                        >
                                            {linkCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                            {linkCopied ? 'Copied!' : 'Copy'}
                                        </button>
                                        <button
                                            onClick={shareNative}
                                            className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-[var(--neon-cyan)] text-black font-bold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all"
                                        >
                                            <Sparkles className="w-5 h-5" />
                                            Share
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* Enter ID Method */}
                            {method === 'id' && step === 1 && (
                                <motion.div
                                    key="id-step1"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="flex flex-col"
                                >
                                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                                        <Users className="w-10 h-10 text-purple-400" />
                                    </div>
                                    <h2 className="text-xl font-bold text-white text-center mb-2">Enter Contact ID</h2>
                                    <p className="text-gray-500 text-center text-sm mb-8">
                                        Paste the unique ID shared by your contact
                                    </p>

                                    <input
                                        type="text"
                                        value={contactId}
                                        onChange={(e) => setContactId(e.target.value.toUpperCase())}
                                        placeholder="e.g. A7X-92B"
                                        className="w-full bg-black border-2 border-white/20 rounded-2xl px-5 py-6 text-white font-mono text-center text-3xl tracking-[0.5em] placeholder-gray-700 focus:border-[var(--neon-cyan)] focus:outline-none mb-8 uppercase shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                                        maxLength={6}
                                    />

                                    {searchError && (
                                        <div className="flex items-center justify-center gap-2 text-red-500 bg-red-500/10 p-3 rounded-xl text-sm font-bold mb-6">
                                            <AlertCircle className="w-4 h-4" /> {searchError}
                                        </div>
                                    )}

                                    <button
                                        onClick={async () => {
                                            if (contactId.length === 6) {
                                                setIsSearching(true);
                                                const user = await contactService.findUserByNid(contactId);
                                                setIsSearching(false);
                                                if (user) {
                                                    setFoundName(user.name);
                                                    setStep(2);
                                                } else {
                                                    setSearchError("NID not found");
                                                }
                                            } else if (contactId.length > 30) {
                                                setStep(2); // Assume UUID is correct for now
                                            } else {
                                                setSearchError("Invalid format");
                                            }
                                        }}
                                        disabled={!contactId.trim() || isSearching}
                                        className="w-full py-4 rounded-2xl bg-[var(--neon-cyan)] text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all flex items-center justify-center gap-2"
                                    >
                                        {isSearching ? <Sparkles className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                                        {isSearching ? 'Verifying...' : 'Verify ID'}
                                    </button>
                                </motion.div>
                            )}

                            {/* Step 2: Set Relationship */}
                            {method === 'id' && step === 2 && (
                                <motion.div
                                    key="id-step2"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                >
                                    <h2 className="text-xl font-bold text-white text-center mb-1">
                                        Add {foundName || 'User'}
                                    </h2>
                                    <p className="text-[var(--neon-cyan)] text-center font-mono text-xs mb-6 tracking-widest">
                                        ID CONFIRMED
                                    </p>

                                    <div className="space-y-3 mb-6">
                                        {relationships.map((r) => (
                                            <button
                                                key={r.id}
                                                onClick={() => setRelationship(r.id)}
                                                className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${relationship === r.id
                                                    ? 'bg-[var(--neon-cyan)]/10 border-[var(--neon-cyan)]/50'
                                                    : 'bg-white/5 border-white/10 hover:border-white/20'
                                                    }`}
                                            >
                                                <span className="text-3xl">{r.icon}</span>
                                                <div className="text-left flex-1">
                                                    <h3 className="font-bold text-white">{r.label}</h3>
                                                    <p className="text-gray-500 text-xs">{r.desc}</p>
                                                </div>
                                                {relationship === r.id && (
                                                    <div className="w-6 h-6 rounded-full bg-[var(--neon-cyan)] flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-black" />
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => {
                                            onAddContact(contactId); // ID already verified/resolved if using logic above? 
                                            // Wait, if I verified and found the user by NID, contactId is still the NID string. 
                                            // onAddContact expecs UUID. 
                                            // Need to pass the RESOLVED uuid.
                                            // I don't have it stored in state? 
                                            // Ah, I set 'foundName' but mostly I need 'foundId'.
                                            // Let's rely on handleSubmitId handling the resolution again OR ensure we pass the right thing.
                                            // I updated handleSubmitId earlier to resolve it again. 
                                            // So calling handleSubmitId is safe.
                                            handleSubmitId();
                                        }}
                                        className="w-full py-4 rounded-2xl bg-[var(--neon-cyan)] text-black font-bold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all flex items-center justify-center gap-2"
                                    >
                                        Send Request <Check className="w-5 h-5" />
                                    </button>
                                </motion.div>
                            )}

                            {/* Step 4: Success */}
                            {step === 4 && (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="flex flex-col items-center text-center py-12"
                                >
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", delay: 0.2 }}
                                        className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(0,255,0,0.3)]"
                                    >
                                        <Check className="w-12 h-12 text-green-400" />
                                    </motion.div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Request Sent!</h2>
                                    <p className="text-gray-400 mb-8">
                                        Waiting for them to accept your connection
                                    </p>
                                    <button
                                        onClick={resetAndClose}
                                        className="px-8 py-3 rounded-2xl bg-[var(--neon-cyan)] text-black font-bold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all"
                                    >
                                        Done
                                    </button>
                                </motion.div>
                            )}

                            {/* QR Scanner */}
                            {method === 'qr' && (
                                <motion.div
                                    key="qr"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="flex flex-col items-center text-center py-6"
                                >
                                    <div id="reader" className="w-full max-w-sm rounded-xl overflow-hidden shadow-[0_0_20px_var(--neon-cyan)] mb-4 bg-black"></div>

                                    <p className="text-gray-400">
                                        Scanning for Protectly codes...
                                    </p>
                                </motion.div>
                            )}

                            {/* Nearby Discovery */}
                            {method === 'nearby' && (
                                <motion.div
                                    key="nearby"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="flex flex-col h-full"
                                >
                                    {isScanningNearby ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="relative w-48 h-48 mb-8">
                                                <motion.div
                                                    className="absolute inset-0 rounded-full border border-yellow-500/30"
                                                    animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                                                    transition={{ duration: 2, repeat: Infinity }}
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                                        <Radar className="w-10 h-10 text-yellow-400" />
                                                    </div>
                                                </div>
                                            </div>
                                            <h3 className="text-lg font-bold text-white mb-2">Scanning Sector...</h3>
                                        </div>
                                    ) : (
                                        <div className="space-y-3 p-2">
                                            <div className="flex justify-between items-end mb-2 px-2">
                                                <h3 className="text-sm font-bold text-yellow-500 uppercase tracking-widest">
                                                    {nearbyUsers.length} SIGNALS FOUND
                                                </h3>
                                                <button onClick={() => setMethod('choose')} className="text-xs text-gray-500 hover:text-white">Cancel</button>
                                            </div>

                                            {nearbyUsers.map(user => (
                                                <motion.div
                                                    key={user.id}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-yellow-500/50 transition-all cursor-pointer group"
                                                    onClick={() => verifyAndAdd(user.id)}
                                                >
                                                    <img
                                                        src={user.avatarUrl || `https://api.dicebear.com/9.x/avataaars/svg?seed=${user.id}`}
                                                        alt={user.name}
                                                        className="w-12 h-12 rounded-full bg-black/50"
                                                    />
                                                    <div className="flex-1">
                                                        <h4 className="font-bold text-white">{user.name}</h4>
                                                        <p className="text-xs text-gray-400">{user.role}</p>
                                                    </div>
                                                    <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-yellow-400" />
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AddContactFlow;
